import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { Redirect } from 'react-router-dom';
import queryString from 'query-string';

import typeCheck from 'common/util/typeCheck';
import {
  UTILITY_USER_ROLE_ANONYMOUS,
  hasRoleAdmin,
  hasRolePartner,
  hasRoleRenter,
  hasRoleAnonymous,
} from 'common/utility/userRoles';

import branch from 'client/core/branch';
import log from 'client/lib/log';
import configureDecoratedComponent from 'client/helpers/configureDecoratedComponent';

const debug = log.debug('sunroom:decorators:allowedUtilityRoles');

export function getUserLandingPage({ userRoles }) {
  if (hasRoleAdmin(userRoles)) {
    return '/utility-profit/admin';
  }
  if (hasRolePartner(userRoles)) {
    return '/utility-profit';
  }
  if (hasRoleRenter(userRoles)) {
    return '/utility-setup/session';
  }
  if (hasRoleAnonymous(userRoles)) {
    return '/utility-profit/login';
  }
  return '/';
}

export default function allowedUtilityRoles(params) {
  typeCheck('params::NonEmptyObject', params);

  const { roles, redirectUrl, addRedirectQueryParam } = params;

  typeCheck('roles::NonEmptyArray', roles);

  return function allowedUtilityRolesDecorator(ComponentToDecorate) {
    @branch({
      utilityUserRoles: ['utilityUser', 'roles'],
    })
    @withRouter
    class DecoratedComponent extends Component {
      static propTypes = {
        utilityUserRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
        tours: PropTypes.array,
        location: PropTypes.object.isRequired,
      };

      componentWillUnmount() {
        this._isUnmounting = true;
      }

      _isAllowed() {
        if (this._isUnmounting) {
          return true;
        }
        const { utilityUserRoles } = this.props;
        if (roles.includes(UTILITY_USER_ROLE_ANONYMOUS)) {
          return roles.some((allowedRole) => utilityUserRoles.includes(allowedRole));
        }
        return (
          hasRoleAdmin(utilityUserRoles) ||
          roles.some((allowedRole) => utilityUserRoles.includes(allowedRole))
        );
      }

      render() {
        const { utilityUserRoles, location } = this.props;
        const locationQuery = queryString.parse(location.search);
        const isAllowed = this._isAllowed();

        const redirectTo =
          locationQuery.next || redirectUrl || getUserLandingPage({ userRoles: utilityUserRoles });

        if (!isAllowed) {
          debug(
            `Current user roles "${utilityUserRoles.join(', ')}" not allowed in ` +
              `"${ComponentToDecorate.displayName}", redirecting to ${redirectTo}`,
          );
        }

        return isAllowed ? (
          <ComponentToDecorate {...this.props} />
        ) : (
          <Redirect
            to={`${redirectTo}${
              addRedirectQueryParam ? '?next=' + encodeURIComponent(location.pathname) : ''
            }`}
          />
        );
      }
    }

    configureDecoratedComponent({
      DecoratedComponent: DecoratedComponent,
      OriginalComponent: ComponentToDecorate,
      displayNameSuffix: 'AllowedUtilityRoles',
    });

    return DecoratedComponent;
  };
}
